import { MouseEvent, ReactNode, useEffect, useState, WheelEventHandler } from 'react';
import * as ReactDOM from 'react-dom';


interface Props {
    isOpen: boolean;
    children: ReactNode;
    backgroundClassName?: string;
    frameClassName?: string;
    frameStyle?: React.CSSProperties;
    contentClassName?: string;
    contentStyle?: React.CSSProperties;
    onCancel?: () => void;
    showCloseX?: boolean;
    cancelOnOutsideClick?: boolean;
    lockBackgroundScroll?: boolean;
    onBackgroundWheel?: WheelEventHandler<HTMLDivElement>;
}

export function ModalDialog({
    isOpen,
    children,
    backgroundClassName,
    frameClassName,
    frameStyle,
    contentClassName,
    contentStyle,
    onCancel,
    showCloseX = true,
    cancelOnOutsideClick = false,
    lockBackgroundScroll = false,
    onBackgroundWheel,
}: Props) {
    const [portalContainer] = useState(document.createElement('div'));


    function handleEscapeKeyPress(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            onCancel?.();
        }
    }

    // Handling click outside the modal frame
    function handleOutsideClick(event: MouseEvent<HTMLDivElement>) {
        if ((event.target as Element).classList.contains('modal-background')) {
            onCancel?.();
        }
    }

    useEffect(() => {
        // Add element to DOM
        if (isOpen) {
            document.body.appendChild(portalContainer);
            if (cancelOnOutsideClick) {
                document.addEventListener('keydown', handleEscapeKeyPress);
            }
        }
        return () => {
            if (portalContainer.parentNode) {
                portalContainer.parentNode.removeChild(portalContainer);
            }
            document.removeEventListener('keydown', handleEscapeKeyPress);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, cancelOnOutsideClick, onCancel, portalContainer]);

    useEffect(() => {
        if (!isOpen || !lockBackgroundScroll) return;

        const previousOverflow = document.body.style.overflow;
        const previousPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = previousOverflow;
            document.body.style.paddingRight = previousPaddingRight;
        };
    }, [isOpen, lockBackgroundScroll]);


    if (!isOpen) return null;
    return ReactDOM.createPortal(
        <div
            className={`modal-background ${backgroundClassName ?? ''}`}
            style={{ display: 'flex', flexDirection: 'column' }}
            onClick={cancelOnOutsideClick ? handleOutsideClick : undefined}
            onWheel={onBackgroundWheel}
        >
            <div className={`modal-frame ${frameClassName}`} style={frameStyle} onClick={e => e.stopPropagation()}>
                {showCloseX && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <span className='close' onClick={onCancel}>&times;</span>
                    </div>
                )}
                <div className={contentClassName} style={contentStyle}>
                    {children}
                </div>
            </div>
        </div>,
        portalContainer
    );
}
